import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Select,
  Badge,
  DataTable,
  DrawerBody,
  DrawerFooter,
  ConfirmDialog,
  useToast,
  type DataTableColumn,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import {
  listUsers,
  deleteUser,
  type UserResponse,
  type CreateUserData,
  type UpdateUserData,
} from "../../services/users.service";
import { listRoles, type RoleResponse } from "../../services/roles.service";

export function UsersPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Data
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<UserResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canCreate = hasPermission("usuarios:criar");
  const canEdit = hasPermission("usuarios:editar");
  const canDelete = hasPermission("usuarios:excluir");

  // Load roles once
  useEffect(() => {
    listRoles({ limit: 100 }).then((r) => setRoles(r.data)).catch(() => {});
  }, []);

  // Load users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listUsers({ page, limit: pageSize, search: search || undefined });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch {
      toast({ variant: "error", title: t("users.load_error") });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, t, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset page on search
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Handlers
  const handleNew = () => navigate("/dashboard/usuarios/novo");
  const handleEdit = (user: UserResponse) => navigate(`/dashboard/usuarios/${user.id}`);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      toast({ variant: "success", title: t("users.delete_success") });
      fetchUsers();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("users.delete_error") });
    } finally {
      setDeleting(false);
    }
  };

  // Table columns
  const columns: DataTableColumn<UserResponse>[] = [
    {
      key: "nome",
      header: t("users.col_name"),
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {row.nome.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-foreground">{row.nome}</p>
            <p className="text-xs text-muted-foreground">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "nivel",
      header: t("users.col_level"),
      render: (row) => (
        <Badge variant="default" size="md">
          {row.nivel === "admin"
            ? t("users.level_admin")
            : row.nivel === "gerente"
              ? t("users.level_manager")
              : t("users.level_operator")}
        </Badge>
      ),
    },
    {
      key: "perfil",
      header: t("users.col_role"),
      render: (row) => {
        const role = row.perfilId ? roles.find((r) => r.id === row.perfilId) : null;
        return role ? (
          <Badge variant="info" size="md">{role.nome}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        );
      },
    },
    {
      key: "ativo",
      header: t("users.col_status"),
      render: (row) => (
        <Badge variant={row.ativo ? "success" : "outline"} dot size="md">
          {row.ativo ? t("common.active") : t("common.inactive")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-24 text-right",
      render: (row) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} className="h-8 w-8 p-0">
              <EditIcon />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(row)}
              className="h-8 w-8 p-0 text-error hover:text-error"
            >
              <TrashIcon />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("users.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("users.count", { count: total })}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleNew}>
            <PlusIcon />
            {t("users.new")}
          </Button>
        )}
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={users}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("users.search_placeholder")}
        emptyMessage={t("users.empty")}
        onRowClick={canEdit ? handleEdit : undefined}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("users.delete_title")}
        description={t("users.delete_message", { name: deleteTarget?.nome })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}

// ============ User Form (Drawer or Page) ============

interface UserFormProps {
  user: UserResponse | null;
  roles: RoleResponse[];
  saving: boolean;
  onSave: (data: CreateUserData | UpdateUserData) => void;
  /** "drawer" (padrão histórico) ou "page" (usado em UserDetailPage). */
  variant?: "drawer" | "page";
}

export function UserForm({ user, roles, saving, onSave, variant = "drawer" }: UserFormProps) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    nome: user?.nome || "",
    email: user?.email || "",
    nivel: user?.nivel || "operador",
    perfilId: user?.perfilId || "",
    senha: "",
    confirmarSenha: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.nome.trim() || formData.nome.length < 3)
      e.nome = t("users.validate_name");
    if (!formData.email.trim() || !formData.email.includes("@"))
      e.email = t("users.validate_email");
    if (!user) {
      if (!formData.senha || formData.senha.length < 6)
        e.senha = t("users.validate_password");
      if (formData.senha !== formData.confirmarSenha)
        e.confirmarSenha = t("users.validate_password_match");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (user) {
      onSave({
        nome: formData.nome,
        email: formData.email,
        nivel: formData.nivel,
        perfilId: formData.perfilId || null,
      } as UpdateUserData);
    } else {
      onSave({
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        nivel: formData.nivel,
        perfilId: formData.perfilId || null,
      } as CreateUserData);
    }
  };

  const nivelOptions = [
    { value: "admin", label: t("users.level_admin") },
    { value: "gerente", label: t("users.level_manager") },
    { value: "operador", label: t("users.level_operator") },
  ];

  const roleOptions = [
    { value: "", label: t("users.no_role") },
    ...roles.map((r) => ({
      value: r.id,
      label: r.nome + (r.descricao ? ` - ${r.descricao}` : ""),
    })),
  ];

  const selectedRole = roles.find((r) => r.id === formData.perfilId);

  const isPage = variant === "page";
  const Body = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="min-h-0 flex-1 overflow-y-auto px-1 py-4">{children}</div>
      )
    : DrawerBody;
  const Footer = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="flex justify-end gap-2 border-t border-border pt-4">{children}</div>
      )
    : DrawerFooter;

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
      <Body>
        <div className="space-y-5">
          {/* Section: Dados Basicos */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("users.section_data")}
            </h3>
            <div className="space-y-4">
              <Input
                label={t("users.label_name")}
                value={formData.nome}
                onChange={(e) => { setFormData({ ...formData, nome: e.target.value }); setErrors({ ...errors, nome: "" }); }}
                error={errors.nome}
                placeholder={t("users.placeholder_name")}
                required
              />
              <Input
                label={t("users.label_email")}
                type="email"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors({ ...errors, email: "" }); }}
                error={errors.email}
                placeholder={t("users.placeholder_email")}
                required
              />
            </div>
          </div>

          {/* Section: Acesso */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("users.section_access")}
            </h3>
            <div className="space-y-4">
              <Select
                label={t("users.label_level")}
                value={formData.nivel}
                onChange={(e) => setFormData({ ...formData, nivel: e.target.value })}
                options={nivelOptions}
              />
              <Select
                label={t("users.role_label")}
                value={formData.perfilId}
                onChange={(e) => setFormData({ ...formData, perfilId: e.target.value })}
                options={roleOptions}
                helperText={
                  selectedRole
                    ? t("users.role_permissions", { list: selectedRole.permissoes.join(", ") || t("users.role_none") })
                    : undefined
                }
              />
            </div>
          </div>

          {/* Section: Senha (only new) */}
          {!user && (
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t("users.section_password")}
              </h3>
              <div className="space-y-4">
                <Input
                  label={t("users.label_password")}
                  type="password"
                  value={formData.senha}
                  onChange={(e) => { setFormData({ ...formData, senha: e.target.value }); setErrors({ ...errors, senha: "" }); }}
                  error={errors.senha}
                  placeholder={t("users.placeholder_min_password")}
                  required
                />
                <Input
                  label={t("users.label_confirm_password")}
                  type="password"
                  value={formData.confirmarSenha}
                  onChange={(e) => { setFormData({ ...formData, confirmarSenha: e.target.value }); setErrors({ ...errors, confirmarSenha: "" }); }}
                  error={errors.confirmarSenha}
                  placeholder={t("users.placeholder_confirm")}
                  required
                />
              </div>
            </div>
          )}
        </div>
      </Body>

      <Footer>
        <Button type="submit" loading={saving}>
          {user ? t("common.save") : t("common.create")}
        </Button>
      </Footer>
    </form>
  );
}

// ============ Icons ============

function PlusIcon() {
  return (
    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
